import { MathObject } from '../mathObjects';
import { Line } from '../mathObjects/Line';
import { toLine } from '../mathObjects/utils/toLine';
import type { ProblemJSDependencies } from '../problemJSDependencies';
import { ReadAloudButton } from '../readAloud/readAloudButton';
import {
  injectReadAloudButton,
  shiftIndexes,
} from '../readAloud/readAloudUtil';
import type { TTSDTO } from '../readAloud/TTS';
import { getReadAloudButtonWidth } from '../state';

import {
  BasicObject,
  getBasicObject,
  getFillerBasicObject,
} from './basicObject';

export const STRINGS_AND_VALUES_PARAGRAPH_GMM_NAME =
  'StringsAndValuesParagraph';

const VERTICAL_SPACE_BETWEEN_LINES = 5;

export interface StringsAndValuesParagraphDTO {
  lines: string[];
}

export interface StringsAndValuesParagraphWrapperDTO {
  xmlGraph: StringsAndValuesParagraphDTO;
  tts?: TTSDTO;
}

// We'll build client-side Lines out of incoming strings (which may be 'xml' or may be plain text).
// Then we'll build a BasicObject that contains all the Lines.
export function getStringsAndValuesParagraph(
  problemJS: ProblemJSDependencies,
  args: StringsAndValuesParagraphWrapperDTO,
  screenMaxTextW: number,
  x = 0,
  targetForReadAloud?: BasicObject
): BasicObject {
  let maxW = screenMaxTextW;

  // Leave room for a ReadAloudButton
  if (problemJS.isReadAloud()) maxW -= getReadAloudButtonWidth();

  const lines: Line[] = [];

  // 'xml' strings or plain text
  const incomingStrings = args.xmlGraph.lines.slice();

  let proposedStringsForOneLine: string[] = [];

  for (
    let indexOfNextIncomingString = 0;
    indexOfNextIncomingString < incomingStrings.length;
    indexOfNextIncomingString++
  ) {
    const nextIncomingString = incomingStrings[indexOfNextIncomingString];

    // If string is plain text, split it into words and find out how many fit in the currently proposed line.
    if (nextIncomingString.indexOf('<') !== 0) {
      // Split the plain text into single words, preserving spaces
      const singleWords: string[] = getSingleWords(nextIncomingString);

      if (singleWords.length === 0) continue;

      let previousProposal = '';
      // this gets longer and longer until we discover the Line is too long
      let proposalString = '';
      let firstWordOfPlainText = true;
      let broke = false;

      for (
        let singleWordIndex = 0;
        singleWordIndex < singleWords.length;
        singleWordIndex++
      ) {
        const nextWord = singleWords[singleWordIndex];

        previousProposal = proposalString;
        proposalString += nextWord;
        proposedStringsForOneLine.push(proposalString);
        const proposedLine = createProposedLine(
          problemJS,
          proposedStringsForOneLine,
          x,
          maxW
        );

        if (proposedLine.viewportW > maxW || proposedLine.hasMultiLinesText()) {
          if (firstWordOfPlainText) {
            if (proposedStringsForOneLine.length === 1) {
              // Woah: this is our ONLY string in our proposal, and it is too long.
              // We need to artificially break this string and start over.
              const forciblyInsertSpaceHere = nextWord.length / 2;
              const replacement =
                nextWord.substring(0, forciblyInsertSpaceHere) +
                ' ' +
                nextWord.substring(forciblyInsertSpaceHere);

              // Replace the incoming string with two strings.
              // Here is the first: an artificially broken word. Second is later...
              incomingStrings.splice(indexOfNextIncomingString, 1, replacement);

              // Make a string out of the remaining words in the incoming string.
              if (singleWordIndex < singleWords.length - 1) {
                let rebuildRemainderOfWords = '';

                for (
                  let remainingWordIndex = singleWordIndex + 1;
                  remainingWordIndex < singleWords.length;
                  remainingWordIndex++
                ) {
                  rebuildRemainderOfWords += singleWords[remainingWordIndex];

                  if (remainingWordIndex != singleWords.length - 1) {
                    rebuildRemainderOfWords += ' ';
                  }
                }

                // Earlier, we swapped the incoming string for just the broken word. Now we also
                // need the rest of the unused words from the original incoming string. Final result:
                // where we originally had one incoming string, we now have two:
                // 1. the broken word, and
                // 2. the rest of the words.
                incomingStrings.splice(
                  indexOfNextIncomingString + 1,
                  0,
                  rebuildRemainderOfWords
                );
              }

              // Start over: re-evaluate starting at the original incoming string index, now that we have forcibly altered the incoming strings.
              indexOfNextIncomingString--;
              proposedStringsForOneLine = [];
            } else {
              // Plain text failed width on its first word, but there was prior content in proposal, so fall back to that proposal
              // then start working on next proposal by re-considering this plain text.
              proposedStringsForOneLine.splice(
                proposedStringsForOneLine.length - 1,
                1
              );
              lines.push(
                createProposedLine(
                  problemJS,
                  proposedStringsForOneLine,
                  x,
                  maxW
                )
              );
              proposedStringsForOneLine = [];
              indexOfNextIncomingString--;
            }
          }
          // When we added the next word of the plain text string, it made the proposed line too long,
          // so fall back to the previous proposal, then replace
          else {
            proposedStringsForOneLine.splice(
              proposedStringsForOneLine.length - 1,
              1
            );
            proposedStringsForOneLine.push(previousProposal);
            lines.push(
              createProposedLine(problemJS, proposedStringsForOneLine, x, maxW)
            );
            proposedStringsForOneLine = [];

            let remainingWords = '';

            for (let i = singleWordIndex; i < singleWords.length; i++) {
              remainingWords += singleWords[i];
            }

            incomingStrings[indexOfNextIncomingString] = remainingWords;
            indexOfNextIncomingString--;
          }

          broke = true;
          break;
        } else {
          // Adding current proposal string fits. Remove it, then loop back for a longer proposal string.
          proposedStringsForOneLine.splice(
            proposedStringsForOneLine.length - 1,
            1
          );

          // Read Aloud extra logic: if beginning of a line is a space, we end up removing it while laying out the line,
          // so we need to shift the displayIndexes array else our highlighting will be off.
          if (
            problemJS.isReadAloud() &&
            args.tts &&
            proposedStringsForOneLine.length === 0 &&
            singleWordIndex == 0 &&
            nextWord === ' '
          ) {
            // get paintedCharCount from all previous lines to know
            // which elements in the displayIndexes array to shift
            let paintedCharCount = 0;

            for (let i = 0; i < lines.length; i++) {
              paintedCharCount += lines[i].getPaintedCharCount();
            }

            // get displayIndexes array from tts object and update it
            // -1 shifts left 1
            shiftIndexes(args.tts.displayIndexes, paintedCharCount, -1);
          }
        }

        firstWordOfPlainText = false;
      }

      // The entire plain text string fits
      if (!broke) {
        proposedStringsForOneLine.push(nextIncomingString);
      }
    }
    // incoming string is xml -- do not break it up
    else {
      proposedStringsForOneLine.push(nextIncomingString);
      let proposedLine = createProposedLine(
        problemJS,
        proposedStringsForOneLine,
        x,
        maxW
      );

      if (proposedLine.viewportW > maxW || proposedLine.hasMultiLinesText()) {
        // This line is too long, but we use it 'as is' because we're not supposed to break up xml.
        if (proposedStringsForOneLine.length === 1) {
          lines.push(proposedLine);
          proposedStringsForOneLine = [];
        } else {
          proposedStringsForOneLine.splice(
            proposedStringsForOneLine.length - 1,
            1
          );
          indexOfNextIncomingString--;
          proposedLine = createProposedLine(
            problemJS,
            proposedStringsForOneLine,
            x,
            maxW
          );
          lines.push(proposedLine);
          proposedStringsForOneLine = [];
        }
      }
    }
  }

  if (proposedStringsForOneLine.length > 0) {
    lines.push(
      createProposedLine(problemJS, proposedStringsForOneLine, x, maxW)
    );
  }

  const ret = new BasicObject();

  ret.gmmName = STRINGS_AND_VALUES_PARAGRAPH_GMM_NAME;

  let lineY = 0;

  for (let a = 0; a < lines.length; a++) {
    const line = lines[a];

    line.viewportY = lineY;
    lineY += line.viewportH;
    ret.add(line);

    if (a != lines.length - 1) {
      const spacer = getBasicObject();

      spacer.setAllDim(1, VERTICAL_SPACE_BETWEEN_LINES);
      spacer.viewportY = lineY;
      lineY += VERTICAL_SPACE_BETWEEN_LINES;
      ret.add(spacer);
    }
  }

  setupReadAloud(problemJS, lines, targetForReadAloud, args);

  ret.sizeMeToFitChildren();

  ret.getVertToBaseline = function (): number {
    if (lines.length === 0) return 0;

    return lines[0].getVertToBaseline();
  };

  ret.isBaselineAware = function (): boolean {
    return true;
  };

  return ret;
}

const createProposedLine = function (
  problemJS: ProblemJSDependencies,
  proposedStrings: string[],
  x: number,
  maxW?: number
): Line {
  let xml = '';

  for (let b = 0; b < proposedStrings.length; b++) {
    let next = proposedStrings[b];

    // No space(s) at start of Line
    if (b == 0) next = next.trimStart();

    // If string is plain text, wrap it in <t> tags for later parsing during toLine.
    if (next.indexOf('<') !== 0) {
      next = '<t>' + next + '</t>';
    }

    xml += next;
  }

  return toLine({
    problemJS,
    xml,
    x,
    y: 0,
    multiline: true,
    // savps are not editable
    editable: false,
    maxW,
  });
};

const getSingleWords = (input: string): string[] => {
  const singleWords: string[] = [];
  let cutter = input;

  while (cutter.length > 0) {
    const spaceIndex = cutter.indexOf(' ');
    const nextSingleWord =
      spaceIndex > -1 ? cutter.substring(0, spaceIndex + 1) : cutter;

    singleWords.push(nextSingleWord);
    cutter = spaceIndex > -1 ? cutter.substring(spaceIndex + 1) : '';
  }

  // excise empty strings from singleWords
  let cutIndex = 0;

  while (cutIndex < singleWords.length) {
    const nextSingleWord = singleWords[cutIndex];

    if (nextSingleWord !== '') {
      cutIndex++;
    } else {
      // remove empty string, then loop to continue checking
      singleWords.splice(cutIndex, 1);
    }
  }

  return singleWords;
};

const setupReadAloud = (
  problemJS: ProblemJSDependencies,
  lines: Line[],
  targetForReadAloud: BasicObject | undefined,
  args: StringsAndValuesParagraphWrapperDTO
): void => {
  if (!(problemJS.isReadAloud() && args.tts)) return;

  let readAloudButton: ReadAloudButton | undefined;

  for (let x = 0; x < lines.length; x++) {
    const nextLine = lines[x];

    // First line starts with a ReadAloudButton.
    // Note to selves: we decided not to use a 'wrapper'
    // strategy because then the button was not vertically centered with
    // the rest of the elements in the first line.
    if (x === 0) {
      readAloudButton = injectReadAloudButton(
        targetForReadAloud || nextLine,
        args.tts,
        problemJS,
        nextLine
      );
    } else {
      // Lines in an xmlGraph do not have their own
      // tts data from the server, since a single tts object
      // models the entire paragraph.
      readAloudButton!.tts.addDisplayedElement(nextLine);

      if (!targetForReadAloud) {
        // Force a matching horizontal space to left of each non-first line
        // to match indent from ReadAloudButton on first line
        const filler = getFillerBasicObject(getReadAloudButtonWidth(), 0);

        nextLine.add(filler as MathObject, 0);
      }
    }

    nextLine.lockViewportDim = false;
    nextLine.buildSizeRecursive();
  }
};
